import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import moment from "moment";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import { SUGGESTION_CATEGORIES } from "../../components/SuggestionModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

export const SUGGESTION_STATUSES = [
  { value: "new", label: "Nova", plural: "Novas", color: "#1a73d7" },
  { value: "analyzing", label: "Em análise", plural: "Em análise", color: "#f29900" },
  { value: "planned", label: "Planejada", plural: "Planejadas", color: "#8e44ad" },
  { value: "done", label: "Implementada", plural: "Implementadas", color: "#1e8e3e" },
  { value: "rejected", label: "Recusada", plural: "Recusadas", color: "#80868b" },
];

const CATEGORY_COLORS = {
  improvement: "#1a73d7",
  bug: "#d93025",
  feature: "#8e44ad",
  other: "#80868b",
};

const statusOf = (value) => SUGGESTION_STATUSES.find((s) => s.value === value);
const categoryLabel = (value) =>
  SUGGESTION_CATEGORIES.find((c) => c.value === value)?.label || value;

// "Hoje, 14:32" / "Ontem, 09:10" / "03/10/2026"
const formatDate = (date) => {
  const m = moment(date);
  if (m.isSame(moment(), "day")) return `Hoje, ${m.format("HH:mm")}`;
  if (m.isSame(moment().subtract(1, "day"), "day")) return `Ontem, ${m.format("HH:mm")}`;
  return m.format("DD/MM/YYYY");
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1100,
    margin: "0 auto",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  summary: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  toolbar: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  search: {
    width: "100%",
    maxWidth: 420,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { maxWidth: "none" },
  },
  tabs: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
  },
  tablePaper: {
    borderRadius: theme.spacing(1),
    overflow: "hidden",
  },
  row: {
    cursor: "pointer",
  },
  rowNew: {
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
  },
  cellTitle: {
    fontWeight: 600,
    maxWidth: 380,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cellPreview: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    maxWidth: 380,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cellSecondary: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  categoryChip: {
    height: 22,
    fontSize: "0.72rem",
    fontWeight: 600,
    border: "1px solid",
    backgroundColor: "transparent",
  },
  metaChip: {
    height: 22,
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
  },
  statusChip: {
    height: 24,
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
  },
  cardPreview: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: 56,
    marginBottom: theme.spacing(1.5),
    opacity: 0.35,
  },
  loadMore: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  detailChips: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  detailText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.6,
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(2),
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.75),
    fontSize: "0.85rem",
    "& dt": { color: theme.palette.text.secondary },
    "& dd": { margin: 0, wordBreak: "break-word" },
    "& a": { color: theme.palette.primary.main },
  },
  screenshot: {
    display: "block",
    maxWidth: "100%",
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
}));

// Chip colorido que abre um menu para trocar o status.
const StatusChip = ({ value, onChange, className }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const status = statusOf(value);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleSelect = (e, newValue) => {
    e.stopPropagation();
    setAnchorEl(null);
    if (newValue !== value) onChange(newValue);
  };

  return (
    <>
      <Chip
        size="small"
        label={status?.label || value}
        onClick={handleOpen}
        onDelete={handleOpen}
        deleteIcon={<ArrowDropDownIcon style={{ color: "#fff", margin: "0 2px 0 -6px" }} />}
        className={className}
        style={{ backgroundColor: status?.color }}
      />
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={(e) => {
          if (e && e.stopPropagation) e.stopPropagation();
          setAnchorEl(null);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {SUGGESTION_STATUSES.map((s) => (
          <MenuItem
            key={s.value}
            selected={s.value === value}
            onClick={(e) => handleSelect(e, s.value)}
            dense
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: s.color,
                marginRight: 10,
              }}
            />
            {s.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const CategoryChip = ({ value, className }) => {
  const color = CATEGORY_COLORS[value] || CATEGORY_COLORS.other;
  return (
    <Chip
      size="small"
      label={categoryLabel(value)}
      className={className}
      style={{ color, borderColor: color }}
    />
  );
};

const Suggestions = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);

  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const newCount = counts.new || 0;
  const inProgressCount = (counts.analyzing || 0) + (counts.planned || 0);

  useEffect(() => {
    if (!user.super) {
      toast.error("Esta página é restrita ao administrador do sistema.");
      history.push("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setRecords([]);
    setPageNumber(1);
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    if (!user.super) return;
    let cancelled = false;

    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/suggestions", {
          params: {
            pageNumber,
            status: statusFilter || undefined,
            searchParam: debouncedSearch || undefined,
          },
        });
        if (cancelled) return;
        setRecords((prev) =>
          pageNumber === 1 ? data.records : [...prev, ...data.records]
        );
        setCounts(data.counts || {});
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      }
      if (!cancelled) setLoading(false);
    };

    fetchSuggestions();
    return () => {
      cancelled = true;
    };
  }, [pageNumber, statusFilter, debouncedSearch, user.super]);

  // Libera o blob do print quando ele é trocado ou o detalhe é fechado.
  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    };
  }, [screenshotUrl]);

  const handleStatusChange = async (suggestion, status) => {
    try {
      const { data } = await api.put(`/suggestions/${suggestion.id}`, { status });
      const matchesFilter = !statusFilter || statusFilter === data.status;
      setRecords((prev) =>
        matchesFilter
          ? prev.map((r) => (r.id === data.id ? data : r))
          : prev.filter((r) => r.id !== data.id)
      );
      setCounts((prev) => ({
        ...prev,
        [suggestion.status]: Math.max(0, (prev[suggestion.status] || 0) - 1),
        [data.status]: (prev[data.status] || 0) + 1,
      }));
      if (selected?.id === data.id) setSelected(data);
      toast.success(`Sugestão marcada como "${statusOf(data.status)?.label}".`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenDetail = async (suggestion) => {
    setSelected(suggestion);
    setScreenshotUrl(null);
    if (!suggestion.mediaPath) return;

    // O print fica numa rota autenticada (não em /public), então é baixado
    // via api com o token e exibido como blob.
    setScreenshotLoading(true);
    try {
      const { data } = await api.get(`/suggestions/${suggestion.id}/media`, {
        responseType: "blob",
      });
      setScreenshotUrl(URL.createObjectURL(data));
    } catch (err) {
      toastError(err);
    }
    setScreenshotLoading(false);
  };

  const handleCloseDetail = () => {
    setSelected(null);
    setScreenshotUrl(null);
  };

  const renderEmpty = () => {
    let msg = "Nenhuma sugestão recebida ainda.";
    if (debouncedSearch) msg = `Nenhuma sugestão encontrada para "${debouncedSearch}".`;
    else if (statusFilter === "new") msg = "Tudo em dia! Nenhuma sugestão nova para analisar.";
    else if (statusFilter) msg = `Nenhuma sugestão com status "${statusOf(statusFilter)?.label}".`;
    return (
      <div className={classes.emptyState}>
        <EmojiObjectsOutlinedIcon className={classes.emptyIcon} />
        <Typography>{msg}</Typography>
      </div>
    );
  };

  const renderCards = () => (
    <div>
      {records.map((s) => (
        <div key={s.id} onClick={() => handleOpenDetail(s)} style={{ cursor: "pointer" }}>
          <MobileRecordCard
            title={s.title}
            subtitle={`${s.company?.name || `Empresa #${s.companyId}`} · ${s.user?.name || "—"}`}
            headerAction={
              <StatusChip
                value={s.status}
                onChange={(status) => handleStatusChange(s, status)}
                className={classes.statusChip}
              />
            }
            body={<Typography className={classes.cardPreview}>{s.text}</Typography>}
            chips={
              <>
                <CategoryChip value={s.category} className={classes.categoryChip} />
                <Chip size="small" variant="outlined" label={formatDate(s.createdAt)} className={classes.metaChip} />
                {s.mediaPath && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<ImageOutlinedIcon style={{ fontSize: 14 }} />}
                    label="Print"
                    className={classes.metaChip}
                  />
                )}
              </>
            }
          />
        </div>
      ))}
      {loading && [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
    </div>
  );

  const renderTable = () => (
    <Paper className={classes.tablePaper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Sugestão</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Enviada por</TableCell>
            <TableCell>Data</TableCell>
            <TableCell align="center">Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((s) => (
            <TableRow
              key={s.id}
              hover
              className={`${classes.row} ${s.status === "new" ? classes.rowNew : ""}`}
              onClick={() => handleOpenDetail(s)}
            >
              <TableCell>
                <div className={classes.cellTitle} title={s.title}>
                  {s.title}
                  {s.mediaPath && (
                    <Tooltip title="Possui print">
                      <ImageOutlinedIcon
                        fontSize="inherit"
                        color="action"
                        style={{ verticalAlign: "middle", marginLeft: 6 }}
                      />
                    </Tooltip>
                  )}
                </div>
                <div className={classes.cellPreview}>{s.text}</div>
              </TableCell>
              <TableCell>
                <CategoryChip value={s.category} className={classes.categoryChip} />
              </TableCell>
              <TableCell>
                <div>{s.user?.name || "—"}</div>
                <div className={classes.cellSecondary}>
                  {s.company?.name || `Empresa #${s.companyId}`}
                </div>
              </TableCell>
              <TableCell style={{ whiteSpace: "nowrap" }}>
                <Tooltip title={moment(s.createdAt).format("DD/MM/YYYY HH:mm")}>
                  <span>{formatDate(s.createdAt)}</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <StatusChip
                  value={s.status}
                  onChange={(status) => handleStatusChange(s, status)}
                  className={classes.statusChip}
                />
              </TableCell>
            </TableRow>
          ))}
          {loading && <TableRowSkeleton columns={5} />}
        </TableBody>
      </Table>
    </Paper>
  );

  return (
    <div className={classes.root}>
      <Dialog
        open={!!selected}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
      >
        {selected && (
          <>
            <DialogTitle disableTypography style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <Typography variant="h6" style={{ flex: 1, wordBreak: "break-word" }}>
                {selected.title}
              </Typography>
              <IconButton size="small" onClick={handleCloseDetail} aria-label="Fechar">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <div className={classes.detailChips}>
                <StatusChip
                  value={selected.status}
                  onChange={(status) => handleStatusChange(selected, status)}
                  className={classes.statusChip}
                />
                <CategoryChip value={selected.category} className={classes.categoryChip} />
              </div>

              <Typography className={classes.detailText}>{selected.text}</Typography>

              <dl className={classes.metaGrid}>
                <dt>Enviada por</dt>
                <dd>
                  {selected.user?.name || "—"}
                  {selected.user?.email && (
                    <>
                      {" · "}
                      <a href={`mailto:${selected.user.email}`}>{selected.user.email}</a>
                    </>
                  )}
                </dd>
                <dt>Empresa</dt>
                <dd>{selected.company?.name || `#${selected.companyId}`}</dd>
                <dt>Data</dt>
                <dd>{moment(selected.createdAt).format("DD/MM/YYYY [às] HH:mm")}</dd>
                <dt>Tela</dt>
                <dd>
                  {/^\/(?!\/)/.test(selected.pageUrl || "") ? (
                    <a href={selected.pageUrl} target="_blank" rel="noopener noreferrer">
                      {selected.pageUrl}
                    </a>
                  ) : (
                    selected.pageUrl || "—"
                  )}
                </dd>
                <dt>Navegador</dt>
                <dd style={{ fontSize: "0.78rem" }}>{selected.userAgent || "—"}</dd>
              </dl>

              {selected.mediaPath &&
                (screenshotUrl ? (
                  <a href={screenshotUrl} target="_blank" rel="noopener noreferrer" title="Abrir em tamanho real">
                    <img src={screenshotUrl} alt={selected.mediaName} className={classes.screenshot} />
                  </a>
                ) : (
                  screenshotLoading && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="textSecondary">
                        Carregando print...
                      </Typography>
                    </div>
                  )
                ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetail} color="primary">
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <div className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          Sugestões de melhoria
        </Typography>
        <Typography className={classes.summary}>
          {total} no total
          {newCount > 0 && (
            <span style={{ color: theme.palette.primary.main, fontWeight: 600, marginLeft: 8 }}>
              · {newCount} nova{newCount !== 1 ? "s" : ""} para analisar
            </span>
          )}
          {inProgressCount > 0 && (
            <span style={{ marginLeft: 8 }}>· {inProgressCount} em andamento</span>
          )}
        </Typography>
      </div>

      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          placeholder="Buscar no título ou na descrição"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <div className={classes.tabs}>
          {[{ value: "", plural: "Todas" }, ...SUGGESTION_STATUSES].map((s) => {
            const n = s.value ? counts[s.value] || 0 : total;
            const active = statusFilter === s.value;
            return (
              <Chip
                key={s.value || "all"}
                label={n > 0 ? `${s.plural} (${n})` : s.plural}
                onClick={() => setStatusFilter(s.value)}
                color={active ? "primary" : "default"}
                variant={active ? "default" : "outlined"}
                size="small"
              />
            );
          })}
        </div>
      </div>

      {!loading && records.length === 0
        ? renderEmpty()
        : isMobile
          ? renderCards()
          : renderTable()}

      {hasMore && !loading && (
        <div className={classes.loadMore}>
          <Button size="small" onClick={() => setPageNumber((prev) => prev + 1)}>
            Carregar mais
          </Button>
        </div>
      )}
    </div>
  );
};

export default Suggestions;
